// src/pages/Admin/PatientForm.tsx
import { useForm } from 'react-hook-form';
import { Form, Button, Row, Col, Alert, Card, Accordion, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { PatientInput, Patient, createPatient, updatePatient, getPatient, sendWelcomeWithRegistration, sendWelcomeEmail } from '../../api/admin';
import { useState, useEffect } from 'react';
import {
  familyStatusOptions,
  educationLevelOptions,
  supportNetworkOptions,
  ethnicityOptions,
  financialStatusOptions,
  livingConditionsOptions,
  relationshipsOptions,
  genderOptions,
  countryCodeOptions,
  getOptionLabel
} from '../../components/form/FormOptions';
import { CustomDatePicker } from '../../components/shared/CustomDatePicker';
import { WelcomeEmailConfirmationModal } from '../../components/registration/WelcomeEmailConfirmationModal';
import './PatientForm.css';

export function PatientForm() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showWelcomeEmailModal, setShowWelcomeEmailModal] = useState(false);
  const [formData, setFormData] = useState<PatientInput | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPatient, setCurrentPatient] = useState<Patient | null>(null);
  const [isPatientFormMode, setIsPatientFormMode] = useState(false); // Track if sending patient form
  const [pendingPatient, setPendingPatient] = useState<Patient | null>(null); // Store created patient for welcome email
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<PatientInput>();

  // Load patient data for editing
  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      getPatient(id)
        .then((patient) => {
          setCurrentPatient(patient);
          // Convert Patient to PatientInput format and set as default values
          // Keep date in YYYY-MM-DD format for date input
          const formatDateForInput = (dateString: string) => {
            if (!dateString) return '';
            // If it's already in YYYY-MM-DD format, return as is
            if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return dateString;
            }
            // If it's in another format, convert to YYYY-MM-DD
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          };

          reset({
            first_name: patient.first_name,
            last_name: patient.last_name,
            date_of_birth: patient.date_of_birth ? formatDateForInput(patient.date_of_birth) : '',
            gender: patient.gender || '',
            family_status: patient.family_status || '',
            number_of_children: patient.number_of_children || 0,
            education_level: patient.education_level || '',
            occupation: patient.occupation || '',
            residence_type: patient.residence_type || '',
            ethnicity: patient.ethnicity || '',
            previous_psychiatric_diagnoses: patient.previous_psychiatric_diagnoses || '',
            psychiatric_hospitalizations: patient.psychiatric_hospitalizations || '',
            medication_history: patient.medication_history || '',
            suicide_history: patient.suicide_history || '',
            family_psychiatric_history: patient.family_psychiatric_history || '',
            substance_use_history: patient.substance_use_history || '',
            chronic_medical_conditions: patient.chronic_medical_conditions || '',
            allergies: patient.allergies || '',
            other_significant_diagnoses: patient.other_significant_diagnoses || '',
            current_medical_monitoring: patient.current_medical_monitoring || '',
            support_network: patient.support_network || '',
            financial_status: patient.financial_status || '',
            living_conditions: patient.living_conditions || '',
            relationships: patient.relationships || '',
            trauma_history: patient.trauma_history || '',
            religious_beliefs: patient.religious_beliefs || '',
            main_complaint: patient.main_complaint || '',
            symptom_duration: patient.symptom_duration || '',
            precipitating_factors: patient.precipitating_factors || '',
            treatment_progress: patient.treatment_progress || ''
          });
        })
        .catch((err) => {
          console.error('Error loading patient:', err);
          setError('Failed to load patient data: ' + (err.message || 'Unknown error'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isEdit, id, reset]);

  const onSubmit = async (data: PatientInput) => {
    // Instead of directly submitting, show confirmation modal
    setFormData(data);
    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    if (!formData) return;
    
    setIsSubmitting(true);
    try {
      setError('');
      setSuccess('');
      
      // Date is already in YYYY-MM-DD format from date input
      const processedData = { ...formData };
      
      if (isEdit) {
        await updatePatient(id!, processedData);
        setSuccess('Patient updated successfully!');
        setShowConfirmModal(false);
        setTimeout(() => navigate(`/admin/patients/view/${id}`), 2000);
      } else {
        await createPatient(processedData);
        setSuccess('Patient created successfully!');
        setShowConfirmModal(false);
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (err: any) {
      console.error('Error saving patient:', err);
      
      // Log validation errors to console but don't show them to user
      if (err.response?.data?.details) {
        console.error('Validation errors:', err.response.data.details);
      }
      
      // Show generic error message to user
      setError('An error occurred while saving the patient. Please try again.');
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
    setFormData(null);
    setIsPatientFormMode(false);
  };

  const handleSendPatientForm = async () => {
    if (!formData || !formData.email) {
      setError('Patient email is required to send the form');
      return;
    }

    setIsSubmitting(true);
    setIsPatientFormMode(true);
    
    try {
      setError('');
      setSuccess('');
      
      // Create the patient record first (like regular creation)
      const processedData = { ...formData };
      console.log('Creating patient with form data:', processedData);
      
      // Create patient in database
      const createdPatient = await createPatient(processedData);
      console.log('Patient created successfully:', createdPatient);
      
      // Store the created patient and show welcome email modal
      setPendingPatient(createdPatient);
      setShowConfirmModal(false);
      setShowWelcomeEmailModal(true);
      
    } catch (err: any) {
      console.error('Error creating patient:', err);
      
      // Log validation errors to console but don't show them to user
      if (err.response?.data?.details) {
        console.error('Validation errors:', err.response.data.details);
      }
      
      setError('Failed to create patient: ' + (err.message || 'Unknown error'));
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
      setIsPatientFormMode(false);
    }
  };

  const handleWelcomeEmailSend = async (email: string, language: string, generatedLink?: {link: string}) => {
    console.log('handleWelcomeEmailSend called with:', { email, language, pendingPatient, formData });
    
    if (!pendingPatient || !formData) {
      console.error('Missing required data:', { pendingPatient: !!pendingPatient, formData: !!formData });
      setError('No patient data available to send welcome email');
      return;
    }

    try {
      setError('');
      console.log('Starting email send process...');
      
      // Validate email
      if (!email || !email.trim()) {
        throw new Error('Email address is required');
      }
      
      // Update formData with the confirmed email
      const finalPatientData: PatientInput = {
        ...formData,
        email: email.trim()
      };
      
      console.log('Sending welcome email with secure registration link to:', email);
      console.log('Final patient data:', finalPatientData);
      console.log('Generated link data:', generatedLink);
      
      if (generatedLink) {
        // We have a secure link - send email with the pre-generated link (OTP will be sent when patient opens the link)
        console.log('Sending welcome email with secure link...');
        
        const result = await sendWelcomeEmail({
          patient_email: email.trim(),
          patient_name: `${finalPatientData.first_name} ${finalPatientData.last_name}`,
          registration_link: generatedLink.link,
          language: language,
          estimated_duration: language === 'el' ? '15-20 λεπτά' : '15-20 minutes'
        });
        
        console.log('Welcome email sent successfully:', result);
        
        if (result.success) {
          setSuccess(`Patient created successfully! Registration email sent to ${email}.`);
        } else {
          throw new Error(result.message || 'Email sending failed');
        }
      } else {
        // Fallback to the old method
        const result = await sendWelcomeWithRegistration(
          finalPatientData,
          language,
          'Δρ. Ιωάννου', // TODO: Get from user context
          'Ιατρική Κλινική', // TODO: Get from settings
          language === 'el' ? '15-20 λεπτά' : '15-20 minutes'
        );
        
        console.log('Welcome email with registration sent:', result);
        
        // Success message
        if (result.email_sent) {
          setSuccess(`Patient created successfully! A welcome email with registration link has been sent to ${email}. The patient can now complete their information using the secure link provided.`);
        } else {
          setSuccess(`Patient created successfully! Registration token created but email delivery failed. You can provide the patient with the registration link: ${result.registration_url}`);
        }
      }
      
      setShowWelcomeEmailModal(false);
      
      // Navigate back after showing success
      setTimeout(() => navigate('/'), 4000);
      
    } catch (err: any) {
      console.error('Error sending welcome email with registration:', err);
      
      // Log detailed error information for debugging
      if (err.response?.data?.detail) {
        console.error('Backend error details:', err.response.data.detail);
      }
      
      let errorMessage = 'Failed to send welcome email with registration: ';
      if (err.response?.data?.detail) {
        errorMessage += err.response.data.detail;
      } else {
        errorMessage += (err.message || 'Unknown error');
      }
      
      setError(errorMessage);
      setShowWelcomeEmailModal(false);
    }
  };

  const handleWelcomeEmailCancel = () => {
    setShowWelcomeEmailModal(false);
    setPendingPatient(null);
    // Show success for patient creation even if email wasn't sent
    setSuccess('Patient created successfully! You can send a welcome email later if needed.');
    setTimeout(() => navigate('/'), 2000);
  };

  return (
    <div 
      className="patient-form-container admin-background"
      style={{
        backgroundImage: 'url(/Login.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Card className="patient-form-card">
        <Card.Header className="patient-form-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">{isEdit ? '✏️ Edit Patient Information' : '➕ New Patient Registration'}</h2>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-light"
                onClick={() => navigate('/')}
                className="d-flex align-items-center"
              >
                <i className="bi bi-house me-2"></i>
                Home
              </Button>
            </div>
          </div>
        </Card.Header>
          <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading patient data...</span>
              </div>
              <p className="mt-3 text-muted">Loading patient information...</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
            <Accordion defaultActiveKey="0">
              {/* Basic & Contact Information */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <strong>👤 Basic & Contact Information</strong>
                </Accordion.Header>
                <Accordion.Body>
                  {/* Display Patient ID and Serial Number for existing patients */}
                  {isEdit && currentPatient && (
                    <Row className="mb-4">
                      <Col md={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="mb-2 text-muted">Patient Information</h6>
                          <div><strong>Patient ID:</strong> <span className="font-monospace">{currentPatient.patient_id}</span></div>
                          <div><strong>Serial Number:</strong> <span className="badge bg-primary fs-6">#{currentPatient.serial_number}</span></div>
                        </div>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Basic Information Section */}
                  <h6 className="text-primary mb-3 border-bottom pb-2">
                    <i className="bi bi-person-fill me-2"></i>
                    Personal Details
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          {...register('first_name', { 
                            required: 'First name is required',
                            minLength: { value: 2, message: 'First name must be at least 2 characters' }
                          })} 
                          isInvalid={!!errors.first_name}
                          placeholder="Enter first name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.first_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          {...register('last_name', { 
                            required: 'Last name is required',
                            minLength: { value: 2, message: 'Last name must be at least 2 characters' }
                          })} 
                          isInvalid={!!errors.last_name}
                          placeholder="Enter last name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.last_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Birth</Form.Label>
                        <CustomDatePicker
                          value={watch('date_of_birth')}
                          onChange={(date) => setValue('date_of_birth', date)}
                          placeholder="Select date of birth"
                        />
                        {errors.date_of_birth && (
                          <Form.Text className="text-danger">
                            {errors.date_of_birth.message}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Gender</Form.Label>
                        <Form.Select {...register('gender')}>
                          <option value="">Select gender...</option>
                          {genderOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Ethnicity</Form.Label>
                        <Form.Select {...register('ethnicity')}>
                          <option value="">Select ethnicity...</option>
                          {ethnicityOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Social Insurance Number (AMKA)</Form.Label>
                        <Form.Control 
                          {...register('amka')} 
                          placeholder="Enter AMKA (11 digits)"
                          maxLength={11}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Contact Information Section */}
                  <h6 className="text-primary mb-3 mt-4 border-bottom pb-2">
                    <i className="bi bi-telephone-fill me-2"></i>
                    Contact Details
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control 
                          type="email"
                          {...register('email')} 
                          placeholder="Enter email address"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Landline Phone</Form.Label>
                        <div className="d-flex">
                          <Form.Select 
                            {...register('landline_country_code')} 
                            className="me-2" 
                            style={{ width: '100px' }}
                            defaultValue="+30"
                          >
                            {countryCodeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control 
                            {...register('landline_phone', {
                              pattern: {
                                value: /^\d{7,15}$/,
                                message: 'Phone number must have 7-15 digits'
                              }
                            })} 
                            placeholder="Enter landline phone number"
                            isInvalid={!!errors.landline_phone}
                          />
                        </div>
                        {errors.landline_phone && (
                          <Form.Text className="text-danger">
                            {errors.landline_phone.message}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Phone</Form.Label>
                        <div className="d-flex">
                          <Form.Select 
                            {...register('mobile_country_code')} 
                            className="me-2" 
                            style={{ width: '100px' }}
                            defaultValue="+30"
                          >
                            {countryCodeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control 
                            {...register('mobile_phone', {
                              pattern: {
                                value: /^\d{7,15}$/,
                                message: 'Phone number must have 7-15digits'
                              }
                            })} 
                            placeholder="Enter mobile phone number"
                            isInvalid={!!errors.mobile_phone}
                          />
                        </div>
                        {errors.mobile_phone && (
                          <Form.Text className="text-danger">
                            {errors.mobile_phone.message}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* Family & Social Information */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <strong>👨‍👩‍👧‍👦 Family & Social Information</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Family Status</Form.Label>
                        <Form.Control as="select" {...register('family_status')}>
                          <option value="">Select status...</option>
                          {familyStatusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Children</Form.Label>
                        <Form.Control 
                          type="number" 
                          min="0" 
                          max="20"
                          {...register('number_of_children', { 
                            valueAsNumber: true,
                            min: { value: 0, message: 'Number cannot be negative' }
                          })} 
                          placeholder="0"
                        />
                        {errors.number_of_children && (
                          <Form.Text className="text-danger">
                            {errors.number_of_children.message}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Education Level</Form.Label>
                        <Form.Control as="select" {...register('education_level')}>
                          <option value="">Select education level...</option>
                          {educationLevelOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Occupation</Form.Label>
                        <Form.Control 
                          {...register('occupation')} 
                          placeholder="Current job or employment status"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Residence Type</Form.Label>
                        <Form.Control 
                          {...register('residence_type')} 
                          placeholder="e.g., Own home, Rental, With family, etc."
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              {/* Medical/Psychiatric History */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <strong>🏥 Medical & Psychiatric History</strong>
                </Accordion.Header>
                <Accordion.Body>
                  {[
                    { key: 'previous_psychiatric_diagnoses', label: 'Previous Psychiatric Diagnoses', placeholder: 'Any previously diagnosed mental health conditions' },
                    { key: 'psychiatric_hospitalizations', label: 'Psychiatric Hospitalizations', placeholder: 'History of mental health hospitalizations' },
                    { key: 'medication_history', label: 'Medication History', placeholder: 'Current and past psychiatric medications' },
                    { key: 'suicide_history', label: 'Suicide History', placeholder: 'History of suicidal thoughts or attempts (confidential)' },
                    { key: 'family_psychiatric_history', label: 'Family Psychiatric History', placeholder: 'Mental health history in family members' },
                    { key: 'substance_use_history', label: 'Substance Use History', placeholder: 'History of alcohol, drugs, or other substances' },
                    { key: 'chronic_medical_conditions', label: 'Chronic Medical Conditions', placeholder: 'Ongoing medical conditions (diabetes, heart disease, etc.)' },
                    { key: 'allergies', label: 'Allergies & Medical Contraindications', placeholder: 'Drug allergies and medical restrictions' },
                    { key: 'other_significant_diagnoses', label: 'Other Significant Diagnoses', placeholder: 'Any other relevant medical conditions' },
                    { key: 'current_medical_monitoring', label: 'Current Medical Monitoring', placeholder: 'Ongoing medical care and monitoring' },
                  ].map((field, index) => (
                    <Form.Group className="mb-3" key={field.key}>
                      <Form.Label>{field.label}</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        {...register(field.key as keyof PatientInput)} 
                        placeholder={field.placeholder}
                      />
                    </Form.Group>
                  ))}
                </Accordion.Body>
              </Accordion.Item>

              {/* Psychosocial Information */}
              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  <strong>🤝 Psychosocial Information</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Support Network</Form.Label>
                        <Form.Control as="select" {...register('support_network')}>
                          <option value="">Select primary support...</option>
                          {supportNetworkOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Text className="text-muted">Primary source of emotional support</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Financial Status</Form.Label>
                        <Form.Control as="select" {...register('financial_status')}>
                          <option value="">Select financial status...</option>
                          {financialStatusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Text className="text-muted">Current financial situation</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Living Conditions</Form.Label>
                        <Form.Control as="select" {...register('living_conditions')}>
                          <option value="">Select living conditions...</option>
                          {livingConditionsOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Text className="text-muted">Quality and safety of housing</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Relationships</Form.Label>
                        <Form.Control as="select" {...register('relationships')}>
                          <option value="">Select relationship quality...</option>
                          {relationshipsOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Text className="text-muted">Overall quality of personal relationships</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Trauma History</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      {...register('trauma_history')} 
                      placeholder="History of abuse, trauma, or significant life events (confidential)"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Religious & Spiritual Beliefs</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={2} 
                      {...register('religious_beliefs')} 
                      placeholder="Religious affiliation, spiritual practices, or beliefs that may be relevant to care"
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              {/* Current Visit Information */}
              <Accordion.Item eventKey="4">
                <Accordion.Header>
                  <strong>📋 Current Visit Information</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Main Complaint</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      {...register('main_complaint')} 
                      placeholder="Primary reason for seeking care - describe main symptoms or concerns"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Symptom Duration & Intensity</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      {...register('symptom_duration')} 
                      placeholder="How long have symptoms been present? How severe are they? Any changes over time?"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Precipitating & Relieving Factors</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      {...register('precipitating_factors')} 
                      placeholder="What makes symptoms better or worse? Any triggering events or situations?"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Treatment Progress</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      {...register('treatment_progress')} 
                      placeholder="Previous treatments tried and their effectiveness. Current treatment response."
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="mt-4 d-flex gap-3 justify-content-end">
              <Button type="button" variant="outline-secondary" onClick={() => navigate('/')}>
                <i className="bi bi-arrow-left me-2"></i>
                Cancel
              </Button>
              <Button type="submit" variant="primary" size="lg">
                <i className="bi bi-check-lg me-2"></i>
                {isEdit ? 'Save Changes' : 'Create Patient'}
              </Button>
            </div>
          </Form>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal 
        show={showConfirmModal} 
        onHide={handleCancelSubmit}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="patient-form-header border-0">
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-3" style={{ fontSize: '1.5rem' }}></i>
            Confirm Patient {isEdit ? 'Update' : 'Creation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="alert alert-info border-0" style={{ 
            backgroundColor: '#e7f3ff', 
            color: '#0c5460',
            borderRadius: '8px'
          }}>
            <i className="bi bi-info-circle me-2"></i>
            Please review the patient information below before {isEdit ? 'updating' : 'creating'} the record.
          </div>
          
          {/* Show patient form workflow option for new patients with email */}
          {!isEdit && formData?.email && (
            <div className="alert alert-success border-0 mb-3 patient-form-workflow-alert">
              <div className="d-flex align-items-start">
                <i className="bi bi-lightbulb me-2 mt-1"></i>
                <div>
                  <strong>Patient Self-Completion Option:</strong>
                  <p className="mb-0 mt-1 small">
                    Since you've provided an email address, you can send the patient a secure form to complete 
                    the remaining details themselves. This saves time and ensures accuracy of personal information.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {formData && (
            <div className="confirmation-summary">
              <Row>
                <Col md={6}>
                  <div className="summary-section mb-3">
                    <h6 className="text-primary mb-2">
                      <i className="bi bi-person-fill me-2"></i>
                      Basic Information
                    </h6>
                    <div className="summary-item">
                      <strong>Name:</strong> {formData.first_name} {formData.last_name}
                    </div>
                    {formData.date_of_birth && (
                      <div className="summary-item">
                        <strong>Date of Birth:</strong> {new Date(formData.date_of_birth).toLocaleDateString()}
                      </div>
                    )}
                    {formData.gender && (
                      <div className="summary-item">
                        <strong>Gender:</strong> {formData.gender}
                      </div>
                    )}
                    {formData.amka && (
                      <div className="summary-item">
                        <strong>AMKA:</strong> {formData.amka}
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="summary-section mb-3">
                    <h6 className="text-primary mb-2">
                      <i className="bi bi-house-fill me-2"></i>
                      Contact & Social
                    </h6>
                    {formData.email && (
                      <div className="summary-item">
                        <strong>Email:</strong> {formData.email}
                      </div>
                    )}
                    {formData.landline_phone && (
                      <div className="summary-item">
                        <strong>Landline:</strong> {formData.landline_country_code || '+30'} {formData.landline_phone}
                      </div>
                    )}
                    {formData.mobile_phone && (
                      <div className="summary-item">
                        <strong>Mobile:</strong> {formData.mobile_country_code || '+30'} {formData.mobile_phone}
                      </div>
                    )}
                    {formData.family_status && (
                      <div className="summary-item">
                        <strong>Family Status:</strong> {getOptionLabel(familyStatusOptions, formData.family_status)}
                      </div>
                    )}
                    {formData.occupation && (
                      <div className="summary-item">
                        <strong>Occupation:</strong> {formData.occupation}
                      </div>
                    )}
                    {formData.education_level && (
                      <div className="summary-item">
                        <strong>Education:</strong> {getOptionLabel(educationLevelOptions, formData.education_level)}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              
              {formData.main_complaint && (
                <div className="summary-section mb-3">
                  <h6 className="text-primary mb-2">
                    <i className="bi bi-clipboard-heart me-2"></i>
                    Main Complaint
                  </h6>
                  <div className="summary-item">
                    <em>"{formData.main_complaint}"</em>
                  </div>
                </div>
              )}
              
              <div className="summary-section">
                <h6 className="text-primary mb-2">
                  <i className="bi bi-check2-square me-2"></i>
                  Data Completeness
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  <span className={`badge ${formData.first_name && formData.last_name ? 'bg-success' : 'bg-warning'}`}>
                    Basic Info {formData.first_name && formData.last_name ? '✓' : '⚠️'}
                  </span>
                  <span className={`badge ${formData.main_complaint ? 'bg-success' : 'bg-secondary'}`}>
                    Visit Reason {formData.main_complaint ? '✓' : '-'}
                  </span>
                  <span className={`badge ${formData.previous_psychiatric_diagnoses || formData.medication_history ? 'bg-success' : 'bg-secondary'}`}>
                    Medical History {formData.previous_psychiatric_diagnoses || formData.medication_history ? '✓' : '-'}
                  </span>
                  <span className={`badge ${formData.support_network || formData.financial_status ? 'bg-success' : 'bg-secondary'}`}>
                    Psychosocial {formData.support_network || formData.financial_status ? '✓' : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <div className="d-flex gap-3 w-100 justify-content-between align-items-center">
            <Button 
              variant="outline-secondary" 
              onClick={handleCancelSubmit}
              disabled={isSubmitting}
              className="modal-btn-back"
            >
              <i className="bi bi-arrow-left me-2"></i>
              <span>Go Back to Edit</span>
            </Button>
            
            <div className="d-flex gap-3">
              {/* Show patient form option only for new patients with email */}
              {!isEdit && formData?.email && (
                <Button 
                  variant="success" 
                  onClick={handleSendPatientForm}
                  disabled={isSubmitting}
                  title="Create patient record and send them a secure form to complete remaining information"
                  className="modal-btn-send-form"
                  style={{ minWidth: '160px' }}
                >
                  {isSubmitting && isPatientFormMode ? (
                    <>
                      <i className="bi bi-envelope-paper me-2"></i>
                      <span>Creating</span>
                      <span className="loading-dots"></span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-envelope-paper me-2"></i>
                      <span>Send Patient Form</span>
                    </>
                  )}
                </Button>
              )}
              
              <Button 
                variant="primary" 
                onClick={handleConfirmedSubmit}
                disabled={isSubmitting}
                className="modal-btn-confirm"
                style={{ minWidth: '150px' }}
              >
                {isSubmitting && !isPatientFormMode ? (
                  <>
                    <i className="bi bi-check-lg me-2"></i>
                    <span>{isEdit ? 'Updating' : 'Creating'}</span>
                    <span className="loading-dots"></span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-2"></i>
                    <span>Confirm {isEdit ? 'Update' : 'Creation'}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Welcome Email Confirmation Modal */}
      <WelcomeEmailConfirmationModal
        show={showWelcomeEmailModal}
        onHide={handleWelcomeEmailCancel}
        onConfirm={handleWelcomeEmailSend}
        patientName={pendingPatient ? `${pendingPatient.first_name} ${pendingPatient.last_name}` : (formData ? `${formData.first_name} ${formData.last_name}` : '')}
        patientEmail={pendingPatient?.email || formData?.email || ''}
        patientData={pendingPatient || formData || {} as PatientInput}
        loading={isSubmitting}
      />
    </div>
  );
}
